import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { editarVersaoBreadcrumbItems } from "data/versao";
import { transformErrors } from "helpers/utils";
import { useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";
import VersaoUrlSelector from "../../../components/common/VersaoUrlSelector";
import InstrucoesCard from "../../../components/common/InstrucoesCard";
import InstallerSwitch from "components/common/InstallerSwitch";
import TinymceEditor from "components/base/TinymceEditor";

type NovaVersaoType = {
  tag: string;
  descricao: string;
  backendUrl: string;
  frontendUrl: string;
  instalador: boolean;
};

const NovaVersao = () => {
  const form = useForm<NovaVersaoType>({
    defaultValues: {
      tag: "",
      descricao: "",
      backendUrl: "",
      frontendUrl: "",
      instalador: false,
    },
  });

  // Extract version tag from URL
  const extractVersionTag = (url: string): string | null => {
    // Look for patterns like v1.2.3 or 1.2.3
    const versionRegex = /[vV]?(\d+\.\d+\.\d+)/;
    const match = url.match(versionRegex);
    return match
      ? match[0].startsWith("v") || match[0].startsWith("V")
        ? match[0]
        : `v${match[1]}`
      : null;
  };

  // Update tag if URLs change and tag is empty
  useEffect(() => {
    const backendUrl = form.watch("backendUrl");
    const frontendUrl = form.watch("frontendUrl");
    const currentTag = form.watch("tag");

    if (!currentTag) {
      // Try to extract version from backend URL first, then frontend URL
      const backendTag = extractVersionTag(backendUrl);
      const frontendTag = extractVersionTag(frontendUrl);
      const tagToUse = backendTag || frontendTag;

      if (tagToUse) {
        form.setValue("tag", tagToUse);
      }
    }
  }, [form.watch("backendUrl"), form.watch("frontendUrl")]);

  const mutation = useMutation<any, any, NovaVersaoType>({
    mutationFn: (data: NovaVersaoType) => {
      return axios.post("/api/admin/versao", data);
    },
    onError: (error) => {
      const formattedErrors = transformErrors(error.response.data.errors);
      for (const [field, error] of Object.entries(formattedErrors)) {
        form.setError(field as any, error as any);
      }
    },
  });

  const handleInstaladorChange = (value: boolean) => {
    form.setValue("instalador", value, { shouldValidate: true });
  };

  const onSubmit: SubmitHandler<NovaVersaoType> = (data) => {
    mutation.mutate(data);
  };

  if (mutation.isSuccess) {
    const id = mutation.data.data.data.id;
    return <Navigate to={`/admin/versoes/${id}`} />;
  }

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb items={editarVersaoBreadcrumbItems()} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Cadastrar uma versão</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Preencha as informações da versão
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                as="a"
                href="/admin/versoes"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <h4 className="mb-3">Informações básicas</h4>
              <Row className="g-3">
                <Form.Group as={Col} xs={12} className="mb-4" controlId="tag">
                  <Form.Label>Tag da Versão</Form.Label>
                  <Form.Control
                    placeholder="Ex: v1.0.0"
                    {...form.register("tag")}
                    required
                    isInvalid={!!form.formState.errors.tag}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.tag?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  xs={12}
                  className="mb-4"
                  controlId="descricao"
                >
                  <TinymceEditor
                    onChange={(value) => form.setValue("descricao", value)}
                    value={form.watch("descricao")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descricao?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <h4 className="mb-3">URLs e Configurações</h4>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <VersaoUrlSelector
                    label="URL do Backend"
                    placeholder="https://api.example.com/backend-v1.0.0.zip"
                    value={form.watch("backendUrl")}
                    onChange={(value) =>
                      form.setValue("backendUrl", value, {
                        shouldValidate: true,
                      })
                    }
                    isInvalid={!!form.formState.errors.backendUrl}
                    errorMessage={
                      form.formState.errors.backendUrl?.message as string
                    }
                    name="backendUrl"
                    register={form.register}
                  />
                </Col>

                <Col xs={12} md={6}>
                  <VersaoUrlSelector
                    label="URL do Frontend"
                    placeholder="https://app.example.com/installer-v1.0.0.exe"
                    value={form.watch("frontendUrl")}
                    onChange={(value) =>
                      form.setValue("frontendUrl", value, {
                        shouldValidate: true,
                      })
                    }
                    isInvalid={!!form.formState.errors.frontendUrl}
                    errorMessage={
                      form.formState.errors.frontendUrl?.message as string
                    }
                    name="frontendUrl"
                    register={form.register}
                  />
                </Col>

                <Col xs={12}>
                  <InstallerSwitch
                    checked={form.watch("instalador")}
                    onChange={handleInstaladorChange}
                    isInvalid={!!form.formState.errors.instalador}
                    errorMessage={
                      form.formState.errors.instalador?.message as string
                    }
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={12} xl={4}>
              <InstrucoesCard />
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </div>
  );
};

export default NovaVersao;
